import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '../../components/SEO';
import FAQSchema from '../../components/FAQSchema';
import BreadcrumbSchema from '../../components/BreadcrumbSchema';
import Section from '../../components/v2/Section';
import FeaturedClientBuild from '../../components/v2/FeaturedClientBuild';
import { CTABlock } from '../../components/v2/CTAButton';
import { getCaseStudy } from '../../data/caseStudies';

const Hero = ({ cs }) => (
  <Section bg="white" testId="cs-hero">
    <nav className="text-sm text-[#64748B]" aria-label="Breadcrumb">
      <Link to="/case-studies" className="hover:text-[#BC516F]">Case studies</Link> <span className="mx-1">/</span> <span>{cs.cardTitle}</span>
    </nav>
    <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest mt-6">Case study &middot; {cs.client}</p>
    <h1 className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight mt-3 max-w-4xl">{cs.h1}</h1>
    <p className="text-lg text-[#475569] mt-6 max-w-3xl leading-relaxed" data-testid="cs-intro">{cs.intro}</p>
    <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="cs-stats">
      {cs.stats.map((s) => (
        <div key={s.l} className="rounded-[14px] bg-[#F6F8FB] border border-[#E2E8F0] p-4">
          <p className="font-display font-bold text-3xl text-transparent bg-clip-text bg-gradient-to-r from-[#A7468A] to-[#BC516F]">{s.n}</p>
          <p className="text-sm text-[#475569] mt-1">{s.l}</p>
        </div>
      ))}
    </div>
  </Section>
);

const Built = ({ cs }) => (
  <Section bg="bg" testId="cs-built">
    <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">What we built</p>
    <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2 max-w-3xl">{cs.builtHeading}</h2>
    <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
      {cs.built.map(({ icon: Icon, t, d, to, cta }) => (
        <div key={t} className="bg-white border border-[#E2E8F0] rounded-[14px] p-6 flex flex-col transition-shadow duration-300 hover:shadow-[0_18px_44px_rgba(30,42,68,0.10)]">
          <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#A7468A] to-[#BC516F] flex items-center justify-center"><Icon className="w-5 h-5 text-white" /></span>
          <h3 className="font-display font-semibold text-[#1E2A44] text-xl mt-4">{t}</h3>
          <p className="text-[#475569] mt-2 leading-relaxed text-[15px] flex-1">{d}</p>
          <Link to={to} className="mt-4 inline-flex items-center gap-1.5 text-[#BC516F] hover:text-[#A7468A] font-semibold text-sm">{cta} <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
      ))}
    </div>
  </Section>
);

const HowItWorks = ({ cs }) => (
  <Section bg="white" testId="cs-how">
    <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">How it runs every week</p>
    <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">One recording. Every channel.</h2>
    <ol className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {cs.steps.map((s, i) => (
        <li key={s.t} className="rounded-[14px] border border-[#E2E8F0] p-6">
          <span className="font-display font-bold text-[#BC516F] text-sm">0{i + 1}</span>
          <h3 className="font-display font-semibold text-[#1E2A44] text-lg mt-1">{s.t}</h3>
          <p className="text-[#475569] mt-1 text-[15px]">{s.d}</p>
        </li>
      ))}
    </ol>
  </Section>
);

const Faq = ({ cs, other }) => (
  <Section bg="bg" testId="cs-faq">
    <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight">Questions {cs.audience} ask</h2>
    <div className="mt-8 space-y-4 max-w-3xl">
      {cs.faqs.map((f) => (
        <details key={f.question} className="group bg-white border border-[#E2E8F0] rounded-[14px] p-5">
          <summary className="cursor-pointer font-display font-semibold text-[#1E2A44] list-none flex justify-between gap-4">{f.question}<span className="text-[#BC516F] transition-transform duration-300 group-open:rotate-45">+</span></summary>
          <p className="text-[#475569] mt-3 leading-relaxed">{f.answer}</p>
        </details>
      ))}
    </div>
    {other && <p className="mt-8 text-[#475569]">Want to see another full build? <Link to={`/case-studies/${other.slug}`} className="text-[#BC516F] hover:text-[#A7468A] font-semibold" data-testid="cs-other-link">Read the {other.cardTitle} case study</Link>, browse <Link to="/results" className="text-[#BC516F] hover:text-[#A7468A] font-semibold">all client results</Link>, or see <Link to="/" className="text-[#BC516F] hover:text-[#A7468A] font-semibold">our full service offering at Audio &amp; Co.</Link></p>}
  </Section>
);

const schemaFor = (cs, url) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: cs.h1,
  description: cs.seoDescription,
  image: `https://audioandco.com/portfolio/${cs.build.folder}/home.jpg`,
  author: { '@type': 'Organization', name: 'Audio & Co.', url: 'https://audioandco.com' },
  publisher: { '@type': 'Organization', name: 'Audio & Co.', url: 'https://audioandco.com', logo: { '@type': 'ImageObject', url: 'https://audioandco.com/og-image.png' } },
  mainEntityOfPage: url,
  about: { '@type': 'Organization', name: cs.build.name, url: cs.build.site },
  speakable: { '@type': 'SpeakableSpecification', cssSelector: ['h1', '[data-testid="cs-intro"]'] },
});

export default function CaseStudy() {
  const { slug } = useParams();
  const cs = getCaseStudy(slug);
  if (!cs) return <Navigate to="/case-studies" replace />;
  const url = `https://audioandco.com/case-studies/${cs.slug}`;
  const other = cs.other && getCaseStudy(cs.other);
  return (
    <main className="font-body text-[#475569] bg-[#F6F8FB]" data-testid={`case-study-${cs.slug}`}>
      <SEO
        title={cs.seoTitle}
        description={cs.seoDescription}
        keywords={cs.keywords}
        image={`https://audioandco.com/portfolio/${cs.build.folder}/home.jpg`}
        imageAlt={cs.build.shots[0].alt}
        type="article"
      />
      <BreadcrumbSchema items={[{ name: 'Home', path: '/' }, { name: 'Case studies', path: '/case-studies' }, { name: cs.cardTitle, path: `/case-studies/${cs.slug}` }]} />
      <FAQSchema faqs={cs.faqs} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaFor(cs, url)) }} />
      <Hero cs={cs} />
      <FeaturedClientBuild build={cs.build} testIdPrefix="cs-showcase" eyebrow="Explore the live build" />
      <Built cs={cs} />
      <HowItWorks cs={cs} />
      <Faq cs={cs} other={other} />
      <Section bg="navy" innerClassName="!py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">Want your whole business in one place?</h2>
          <p className="text-white/75 mt-4 text-lg">30-minute call. No pitch, no pressure. 4-week minimum, cancel any time.</p>
          <div className="mt-7 flex justify-center"><CTABlock size="lg" align="center" invert testIdPrefix="cs-cta" /></div>
        </div>
      </Section>
    </main>
  );
}
